import { useEffect, useRef } from "react";
import { useT } from "../hooks/useT";

const BLUEPRINT_IMG = "/images/governance_blueprint.png";

export default function GovernanceSection() {
  const t = useT();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.08 }
    );
    ref.current?.querySelectorAll(".fade-in, .clip-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="governance" ref={ref} data-testid="governance-section" className="relative bg-[var(--bg-warm)]">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left — Architectural blueprint panel (desktop only) */}
        <div className="hidden lg:block relative" style={{ backgroundColor: '#0a0908', minHeight: '100vh' }}>
          {/* Blueprint image layer */}
          <img
            src={BLUEPRINT_IMG}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: 'brightness(0.35) contrast(1.1)',
              opacity: 0.55,
              mixBlendMode: 'screen',
            }}
          />

          {/* Darkening overlay for subtlety */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse 90% 80% at 50% 50%, transparent 0%, rgba(10,9,8,0.5) 60%, rgba(10,9,8,0.85) 100%)',
          }} />

          {/* Fine grain noise */}
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.2 }}>
            <defs>
              <filter id="governance-grain">
                <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" seed="19" stitchTiles="stitch" />
                <feColorMatrix type="saturate" values="0" />
                <feComponentTransfer>
                  <feFuncR type="linear" slope="0.05" intercept="0.01" />
                  <feFuncG type="linear" slope="0.05" intercept="0.01" />
                  <feFuncB type="linear" slope="0.05" intercept="0.01" />
                </feComponentTransfer>
              </filter>
            </defs>
            <rect width="100%" height="100%" filter="url(#governance-grain)" />
          </svg>

        </div>

        {/* Right — Raw governance text */}
        <div className="flex flex-col justify-center px-6 md:px-10 lg:px-16 py-24 lg:py-32 lg:border-l" style={{ borderColor: 'var(--rule)' }}>
          <div className="fade-in max-w-[560px]" data-testid="governance-content">
            <div className="flex items-center gap-3 mb-6 lg:hidden">
              <div className="w-px h-4" style={{ backgroundColor: 'var(--gold)', opacity: 0.3 }} />
              <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-[var(--text-tertiary)]">03</p>
            </div>

            <h2
              data-testid="governance-heading"
              className="clip-reveal font-serif text-3xl sm:text-4xl tracking-[0.06em] text-[var(--text-primary)] mb-20"
              style={{ fontWeight: 300 }}
            >
              {t.governance.heading}
            </h2>

            <p className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75] mb-8">
              {t.governance.block1}
            </p>

            <p className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75]">
              {t.governance.block2}
            </p>
          </div>
        </div>
      </div>

      {/* Section edge bleed to cool inquiry */}
      <div className="absolute bottom-0 left-0 right-0 h-[40px] pointer-events-none z-[1]"
        style={{ background: 'linear-gradient(to bottom, transparent, #0a0a0a)' }} />
    </section>
  );
}
